import React, { useState } from "react";
import { Box, Button, Grid, IconButton, styled, Typography, useTheme, Link, Container } from "@mui/material";
import CommonFormField from "../../common/components/CommonFormField";
import { validateEmail } from "../../common/const";
import CommonButton from "../../common/components/CommonButton";
import { Call, East, Email, Facebook, Instagram, LinkedIn, LocationOn, Twitter, YouTube } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../features/authSlice";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import { CreateSubscriber } from "../../api/staticPageApi";
import CommonBookSession from '../../common/components/CommonBookSession';

const Links = styled(Link)({
    textDecoration: 'none',
    color: 'white',
    fontSize: 14,
    cursor: 'pointer',
    transition: 'color 0.3s ease',
    display: 'inline-block',
    marginBottom: '8px'
});

const Footer = () => {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const { loading } = useSelector((state) => state.auth);
    const dispatch = useDispatch();
    const [formData, setFormData] = useState({
        email: ''
    });
    const [errors, setErrors] = useState({});

    const validateField = (name, value) => {
        switch (name) {
            case 'email':
                if (!value) return 'Email is required';
                if (!validateEmail(value)) return 'Please enter a valid email address';
                return '';
            default:
                return '';
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (errors[name]) {
            const error = validateField(name, value);
            if (!error) {
                setErrors(prev => ({ ...prev, [name]: undefined }));
            } else {
                setErrors(prev => ({ ...prev, [name]: error }));
            }
        }
    };

    const validateForm = () => {
        const newErrors = {};
        Object.keys(formData).forEach(key => {
            const error = validateField(key, formData[key]);
            if (error) newErrors[key] = error;
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    const handleSubmit = async () => {
        try {
            if (!validateForm()) return;
            dispatch(setLoading({ loading: true }));
            const response = await CreateSubscriber({
                "email": formData.email,
                "source": "website"
            });
            if (response.success) {
                showNotification(response.message, 'success');
                dispatch(setLoading({ loading: false }));
                setFormData({ email: '' });
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({ loading: false }));
        }
    }

    const socialMediaLinks = {
        instagram: "https://www.instagram.com/verda/",
        twitter: "https://twitter.com/verda",
        linkedin: "https://www.linkedin.com/company/verda",
        youtube: "https://www.youtube.com/verda",
        facebook: "https://www.facebook.com/verda"
    };

    const handleSocialClick = (platform) => {
        window.open(socialMediaLinks[platform], '_blank', 'noopener,noreferrer');
    };

    return (
        <Box
            id="footer"
            sx={{
                minHeight: { xs: 'auto', md: '125vh' },
                height: 'auto',
                width: '100%',
                position: 'relative',
                overflow: 'hidden',
                pb: { xs: 4, md: 0 }
            }}
        >
            {loading && <LoadingSpinner />}
            <CommonBookSession
                bookingUrl='https://calendly.com/bixjet-dreamcollegepath/30min'
                open={open}
                setOpen={setOpen}
            />

            {/* Background Color */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 1,
                    background: theme.palette.secondary.main,
                }}
            />

            {/* Main Content Container */}
            <Container
                maxWidth="xl"
                sx={{
                    position: 'relative',
                    zIndex: 2,
                    px: { xs: 2, sm: 3, md: 4 }
                }}
            >
                {/* Hero Image Section */}
                <Box
                    sx={{
                        position: 'relative',
                        width: '100%',
                        height: { xs: '300px', sm: '350px', md: '400px', lg: '450px' },
                        marginTop: { xs: 4, sm: 6, md: 8, lg: 10 },
                        borderRadius: 2,
                        overflow: 'hidden',
                    }}
                >
                    <img
                        src="/Images/footerimage1.png"
                        alt="footer"
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                        }}
                    />

                    {/* Text Overlay */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: { xs: '20px', sm: '40px', md: '60px', lg: '80px' },
                            transform: 'translateY(-50%)',
                            width: { xs: '90%', sm: '80%', md: '60%', lg: '50%' },
                            maxWidth: '800px',
                            zIndex: 3,
                            pr: { xs: 2, sm: 3, md: 4 }
                        }}
                    >
                        <Typography
                            sx={{
                                color: 'white',
                                fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.8rem', lg: '3.2rem' },
                                fontWeight: 'bold',
                                lineHeight: 1.2,
                                mb: { xs: 1.5, sm: 2, md: 2.5 }
                            }}
                        >
                            Ready to take the next
                            <br />
                            step in your College journey?
                        </Typography>

                        <Typography
                            sx={{
                                color: 'white',
                                fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem', lg: '1.2rem' },
                                fontWeight: 500,
                                lineHeight: 1.6,
                                mb: { xs: 2, sm: 2.5, md: 3 },
                                maxWidth: '600px'
                            }}
                        >
                            Discover how Dream College Planner can shape your future with smart tools, expert guidance, and personalized support to help you achieve your goals.
                        </Typography>

                        <Button
                            variant="contained"
                            onClick={() =>
                                setOpen(true)
                            }
                            endIcon={<East />}
                            sx={{
                                height: { xs: '44px', sm: '48px', md: '52px', lg: '55px' },
                                width: { xs: '100%', sm: 'auto' },
                                minWidth: { sm: '250px', md: '280px' },
                                backgroundColor: 'primary.main',
                                color: 'secondary.main',
                                '&:hover': {
                                    backgroundColor: 'primary.main',
                                    color: 'secondary.main',
                                    boxShadow: 'none'
                                },
                                fontWeight: 600,
                                border: 'none',
                                boxShadow: 'none',
                                fontSize: { xs: '15px', sm: '16px', md: '17px', lg: '18px' },
                                padding: '0 24px',
                            }}
                        >
                            Book DCP Planner Demo
                        </Button>
                    </Box>
                </Box>

                {/* Footer Content Grid */}
                <Grid
                    container
                    spacing={{ xs: 3, sm: 4, md: 5 }}
                    sx={{
                        mt: { xs: 4, sm: 5, md: 6 },
                        pb: { xs: 3, sm: 4, md: 5 },
                        borderBottom: '1px solid #FFFFFF2B'
                    }}
                >
                    {/* Left Column - Company Info */}
                    <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                            <img
                                src="/Images/footerlogo1.png"
                                alt="footerlogo"
                                style={{
                                    maxWidth: '200px',
                                    height: 'auto',
                                    marginBottom: '16px'
                                }}
                            />
                            <Typography
                                color="white"
                                fontSize={{ xs: 14, sm: 15, md: 16 }}
                                sx={{
                                    mb: 2,
                                    px: { xs: 2, md: 0 }
                                }}
                            >
                                Discover how Dream College Planner can shape your future with smart tools, expert guidance, and personalized support to help you achieve your goals.
                            </Typography>

                            {/* Contact Info */}
                            <Box sx={{ px: { xs: 2, md: 0 } }}>
                                <Box display="flex" gap={1} alignItems="center" sx={{ mb: 1.5 }}>
                                    <LocationOn sx={{ color: theme.palette.primary.main, fontSize: { xs: 18, md: 20 } }} />
                                    <Typography color="white" fontSize={{ xs: 13, md: 14 }}>6200 Village Park Way, Dublin, California, United States</Typography>
                                </Box>
                                <Box display="flex" gap={1} alignItems="center" sx={{ mb: 1.5 }}>
                                    <Email sx={{ color: theme.palette.primary.main, fontSize: { xs: 18, md: 20 } }} />
                                    <Typography color="white" fontSize={{ xs: 13, md: 14 }}>admin@dreamcollegepath.com</Typography>
                                </Box>
                                <Box display="flex" gap={1} alignItems="center">
                                    <Call sx={{ color: theme.palette.primary.main, fontSize: { xs: 18, md: 20 } }} />
                                    <Typography color="white" fontSize={{ xs: 13, md: 14 }}>+1 925 307 7711</Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>

                    {/* Right Column - Links and Newsletter */}
                    <Grid item xs={12} md={8}>
                        <Grid container spacing={{ xs: 3, sm: 4 }}>
                            {/* Spacer Column - visible only on desktop */}
                            <Grid item xs={0} md={2} sx={{ display: { xs: 'none', md: 'block' } }} />

                            {/* About Us Links */}
                            <Grid
                                item
                                xs={6}
                                sm={4}
                                md={3}
                            >
                                <Typography
                                    color={theme.palette.primary.main}
                                    fontWeight={600}
                                    fontSize={{ xs: 16, md: 17 }}
                                    sx={{ mb: 1.5 }}
                                >
                                    About Us
                                </Typography>
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                >
                                    <Links href="/my-dcp-about-us">DCP About</Links>
                                    <Links href="/blogs">Blog</Links>
                                    <Links href='/contact-us'>Contact Us</Links>
                                </Box>
                            </Grid>

                            {/* Useful Links */}
                            <Grid item xs={6} sm={4} md={3}>
                                <Typography
                                    color={theme.palette.primary.main}
                                    fontWeight={600}
                                    fontSize={{ xs: 16, md: 17 }}
                                    sx={{ mb: 1.5 }}
                                >
                                    Useful Links
                                </Typography>
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                >
                                    <Links href="/my-dcp-privacy-policy">DCP Privacy & Policy</Links>
                                    <Links href='/my-dcp-terms-conditions'>DCP Terms & Conditions</Links>
                                    <Links href='/my-dcp-faq'>DCP FAQs</Links>
                                </Box>
                            </Grid>

                            {/* Newsletter */}
                            <Grid item xs={12} sm={4} md={4}>
                                <Typography
                                    color={theme.palette.primary.main}
                                    fontWeight={600}
                                    fontSize={{ xs: 16, md: 17 }}
                                    sx={{ mb: 1.5 }}
                                >
                                    Newsletter
                                </Typography>
                                <Typography
                                    color="white"
                                    fontSize={{ xs: 13, md: 14 }}
                                    sx={{ mb: 2 }}
                                >
                                    Stay updated with the latest news and events. Subscribe to our newsletter for exclusive content.
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        gap: 1,
                                    }}
                                >
                                    <Box sx={{ flex: 1 }}>
                                        <CommonFormField
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            error={errors.email}
                                            placeholder="Your Email Address"
                                            sx={{
                                                '& .MuiInputBase-root': {
                                                    height: '44px',
                                                    backgroundColor: 'rgba(255,255,255,0.1)',
                                                    color: 'white'
                                                }
                                            }}
                                        />
                                    </Box>
                                    <CommonButton
                                        label='Send'
                                        width={{ xs: '100%', sm: '80px' }}
                                        height="40px"
                                        fontWeight={600}
                                        handleClick={handleSubmit}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

                {/* Bottom Bar - Copyright and Social Media */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: { xs: 2, sm: 0 },
                        py: { xs: 2, sm: 3, md: 4 },
                    }}
                >
                    <Typography
                        sx={{
                            color: 'white',
                            fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
                            fontWeight: 500,
                            textAlign: { xs: 'center', sm: 'left' }
                        }}
                    >
                        Copyright © 2026 Dream college path
                    </Typography>

                    <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 } }}>
                        <IconButton onClick={() => handleSocialClick('instagram')} sx={{ color: 'white', '&:hover': { color: theme.palette.primary.main } }}>
                            <Instagram fontSize="small" />
                        </IconButton>
                        <IconButton onClick={() => handleSocialClick('twitter')} sx={{ color: 'white', '&:hover': { color: theme.palette.primary.main } }}>
                            <Twitter fontSize="small" />
                        </IconButton>
                        <IconButton onClick={() => handleSocialClick('linkedin')} sx={{ color: 'white', '&:hover': { color: theme.palette.primary.main } }}>
                            <LinkedIn fontSize="small" />
                        </IconButton>
                        <IconButton onClick={() => handleSocialClick('youtube')} sx={{ color: 'white', '&:hover': { color: theme.palette.primary.main } }}>
                            <YouTube fontSize="small" />
                        </IconButton>
                        <IconButton onClick={() => handleSocialClick('facebook')} sx={{ color: 'white', '&:hover': { color: theme.palette.primary.main } }}>
                            <Facebook fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>
            </Container>
        </Box>
    )
}

export default Footer;